const paragraph = (text) => ({ type: 'paragraph', text });
const list = (items) => ({ type: 'list', items });
const links = (items) => ({ type: 'links', links: items });

const mlabPrivacyLink = {
  label: 'Measurement Lab Privacy Policy',
  href: 'https://www.measurementlab.net/privacy/',
};

const firebasePrivacyLink = {
  label: 'Firebase Privacy and Security',
  href: 'https://firebase.google.com/support/privacy',
};

const googlePrivacyLink = {
  label: 'Google Privacy Policy',
  href: 'https://policies.google.com/privacy',
};

const googleAdsLink = {
  label: 'Google Advertising Policies',
  href: 'https://policies.google.com/technologies/ads',
};

export const speedTestPrivacy = {
  effectiveDate: 'July 31, 2026',
  lastUpdated: 'July 31, 2026',
  title: 'Privacy Policy',
  intro: 'This Privacy Policy explains how Speed Test handles information when you use the App.',
  sections: [
    {
      title: 'Introduction',
      blocks: [
        paragraph('This Privacy Policy explains how Speed Test (“the App”), provided by 2xHappiness (“we,” “us,” or “our”), collects, uses, stores, and shares information when you use the App.'),
        paragraph('By using Speed Test, you acknowledge the practices described in this Privacy Policy.'),
      ],
    },
    {
      title: '1. Information We Process',
      blocks: [
        paragraph('The following sections describe the categories of information processed by Speed Test and the third-party services it uses.'),
      ],
    },
    {
      title: '1.1 Network Measurement Data',
      blocks: [
        paragraph('Speed Test uses Measurement Lab (“M-Lab”) infrastructure to perform network speed measurements.'),
        paragraph('When you start a speed or latency test, the App connects directly to M-Lab servers and transfers synthetic test data. M-Lab may process and publish information associated with the test, including:'),
        list([
          'Your public IP address',
          'Download and upload measurements',
          'Network latency and related measurement results',
          'Test date and time',
          'Information about the M-Lab server used',
          'Technical network measurement data',
        ]),
        paragraph('M-Lab makes measurement data publicly available for Internet research and transparency purposes. M-Lab may retain and publish this data according to its own policies.'),
        paragraph('We do not control M-Lab’s infrastructure or public datasets. Please review M-Lab’s privacy policy before running a test:'),
        links([mlabPrivacyLink]),
      ],
    },
    {
      title: '1.2 Information Stored Locally',
      blocks: [
        paragraph('The App stores certain information locally on your device, including:'),
        list([
          'Speed test history',
          'Download, upload, and latency results',
          'Preferred language',
          'App settings and preferences',
          'Onboarding and consent status',
          'A randomly generated installation identifier used for analytics',
        ]),
        paragraph('Local test history is not automatically uploaded to our own servers. It remains on your device until you clear the App’s data or uninstall the App.'),
      ],
    },
    {
      title: '1.3 Analytics Information',
      blocks: [
        paragraph('We use Firebase Analytics to understand how the App is used, maintain performance, and improve features.'),
        paragraph('Analytics information may include:'),
        list([
          'App launches and screen views',
          'App interactions and feature usage',
          'Session and installation identifiers',
          'Device platform and selected language',
          'App version and device information',
          'Advertising interactions',
          'Error and diagnostic information',
        ]),
        paragraph('We do not intentionally send your locally stored test history to Firebase Analytics.'),
        paragraph('Firebase and Google may automatically process device identifiers, IP addresses, approximate location derived from an IP address, and other technical information according to their policies.'),
        paragraph('More information is available at:'),
        links([firebasePrivacyLink, googlePrivacyLink]),
      ],
    },
    {
      title: '1.4 Advertising Information',
      blocks: [
        paragraph('Speed Test displays advertisements using Google Mobile Ads.'),
        paragraph('Google Mobile Ads may automatically collect or share information such as:'),
        list([
          'IP address',
          'Advertising ID or other device identifiers',
          'App and advertisement interactions',
          'Ad impressions, clicks, and video views',
          'Device and account identifiers',
          'Diagnostic and performance information',
          'Approximate location derived from an IP address',
        ]),
        paragraph('This information may be used for advertising, analytics, fraud prevention, security, and ad measurement. Advertising personalization depends on your region, consent choices, device settings, and Google’s policies.'),
        paragraph('You can review Google’s advertising practices at:'),
        links([googleAdsLink]),
      ],
    },
    {
      title: '1.5 Network Connection Information',
      blocks: [
        paragraph('The App checks whether your device appears to be connected through Wi-Fi, Ethernet, mobile data, or another connection type.'),
        paragraph('This information is used to provide data-usage warnings before starting a test. The App does not use this feature to read Wi-Fi passwords or inspect the content of your Internet traffic.'),
      ],
    },
    {
      title: '2. How We Use Information',
      blocks: [
        paragraph('We use information to:'),
        list([
          'Provide download, upload, latency, and jitter measurements',
          'Display and maintain your local test history',
          'Select available measurement infrastructure',
          'Warn you about possible mobile or metered data usage',
          'Maintain, troubleshoot, and improve the App',
          'Understand feature usage and App performance',
          'Display and measure advertisements',
          'Prevent fraud, abuse, and technical problems',
          'Comply with applicable legal obligations',
        ]),
      ],
    },
    {
      title: '3. How Information Is Shared',
      blocks: [
        paragraph('Information may be shared with:'),
        list([
          'Measurement Lab, when you start a network measurement',
          'Google Firebase, for analytics and diagnostics',
          'Google Mobile Ads and advertising partners, for advertising and related purposes',
          'Service providers that help us operate and maintain the App',
          'Government authorities or other parties when required by applicable law',
          'A successor organization if the App or related business is transferred, merged, or acquired',
        ]),
        paragraph('Each third-party provider processes information according to its own privacy policies and terms.'),
      ],
    },
    {
      title: '4. Data Retention',
      blocks: [
        paragraph('Local test history and App preferences remain on your device until you clear the App’s data or uninstall the App.'),
        paragraph('Firebase Analytics and Google Mobile Ads retain information according to Google’s retention settings and policies.'),
        paragraph('M-Lab may retain and publicly publish network measurement data according to its privacy and data-retention policies. We cannot directly remove information from M-Lab’s systems.'),
      ],
    },
    {
      title: '5. Your Choices and Rights',
      blocks: [
        paragraph('Depending on your location, you may have rights to access, correct, delete, restrict, or object to certain processing of your personal information.'),
        paragraph('You may also:'),
        list([
          'Delete locally stored information by clearing App data or uninstalling the App',
          'Reset or delete your device’s advertising identifier through system settings',
          'Control advertising personalization through your Google or device settings',
          'Decline to start an M-Lab network test',
          'Contact us regarding privacy requests',
        ]),
        paragraph('Requests concerning data controlled by M-Lab or Google may need to be submitted directly to those providers.'),
      ],
    },
    {
      title: '6. International Data Transfers',
      blocks: [
        paragraph('M-Lab, Google, and other service providers may process information in countries other than the country where you live. Those countries may have different data-protection laws.'),
        paragraph('Where required, providers use appropriate legal safeguards for international transfers.'),
      ],
    },
    {
      title: '7. Children’s Privacy',
      blocks: [
        paragraph('Speed Test is not specifically designed for children under 13 or the minimum digital-consent age required in their country.'),
        paragraph('We do not knowingly request names, email addresses, or other directly identifying information from children. If you believe a child has provided personal information through the App, please contact us.'),
      ],
    },
    {
      title: '8. Security',
      blocks: [
        paragraph('We use reasonable technical and organizational measures to protect information processed through the App. Network communications with supported third-party services generally use encrypted connections.'),
        paragraph('However, no electronic transmission or storage system can be guaranteed to be completely secure.'),
      ],
    },
    {
      title: '9. Changes to This Privacy Policy',
      blocks: [
        paragraph('We may update this Privacy Policy when the App, applicable law, or third-party services change.'),
        paragraph('The updated version will be published with a revised “Last Updated” date. Continued use of the App after an update means the revised policy will apply.'),
      ],
    },
    {
      title: '10. Contact Us',
      blocks: [
        paragraph('If you have questions or requests concerning this Privacy Policy, contact:'),
        paragraph('2xHappiness'),
        paragraph('Email: yuw558071@gmail.com'),
        paragraph('Jurisdiction: Laws applicable to the operator.'),
      ],
    },
  ],
};

export const speedTestTerms = {
  effectiveDate: 'July 31, 2026',
  lastUpdated: 'July 31, 2026',
  title: 'Terms of Service',
  intro: 'These Terms govern your access to and use of Speed Test.',
  sections: [
    {
      title: 'Introduction',
      blocks: [
        paragraph('These Terms of Service (“Terms”) govern your access to and use of Speed Test (“the App”), provided by 2xHappiness (“we,” “us,” or “our”).'),
        paragraph('By downloading, accessing, or using the App, you agree to these Terms. If you do not agree, do not use the App.'),
      ],
    },
    {
      title: '1. Description of the App',
      blocks: [
        paragraph('Speed Test provides tools for measuring and understanding network performance, including:'),
        list([
          'Download and upload speed measurements',
          'TCP connection latency and jitter measurements',
          'Connection-failure indicators',
          'Locally stored test history',
          'General Wi-Fi and network guidance',
        ]),
        paragraph('Some functions depend on third-party services, network availability, device capabilities, and geographic availability.'),
      ],
    },
    {
      title: '2. Network Tests and Data Usage',
      blocks: [
        paragraph('A complete speed test transfers synthetic data between your device and a test server. A single test may use hundreds of megabytes of data.'),
        paragraph('You are responsible for:'),
        list([
          'Checking whether your connection is metered',
          'Understanding your mobile or Internet service plan',
          'Any carrier, roaming, overage, or data charges',
          'Obtaining permission before testing a network you do not own',
          'Ensuring that testing is permitted by your network provider',
        ]),
        paragraph('The App may provide data-usage warnings, but these warnings cannot identify every metered, shared, tethered, or restricted network.'),
      ],
    },
    {
      title: '3. Measurement Lab',
      blocks: [
        paragraph('Speed and latency measurements use infrastructure provided by Measurement Lab (“M-Lab”).'),
        paragraph('When you start a test, your device connects to M-Lab, and M-Lab may collect and publicly publish measurement information, including the public IP address used during the test.'),
        paragraph('Your use of M-Lab infrastructure is also subject to M-Lab’s applicable policies:'),
        links([
          mlabPrivacyLink,
          {
            label: 'Measurement Lab Acceptable Use Policy',
            href: 'https://www.measurementlab.net/aup/',
          },
        ]),
        paragraph('We do not own, operate, or control M-Lab and are not responsible for its availability, datasets, retention practices, or service changes.'),
      ],
    },
    {
      title: '4. Accuracy of Results',
      blocks: [
        paragraph('Network measurements are estimates taken under the conditions present at the time of testing.'),
        paragraph('Results may be affected by:'),
        list([
          'Device hardware and software',
          'Wi-Fi signal strength and interference',
          'Router and modem performance',
          'Mobile network conditions',
          'Background downloads or connected devices',
          'Virtual private networks, proxies, or firewalls',
          'Test server availability and distance',
          'Internet service provider routing or congestion',
        ]),
        paragraph('We do not guarantee that results will be exact, continuous, complete, or identical to measurements produced by another service.'),
        paragraph('Results are provided for informational purposes only. They are not an official certification of service quality and do not guarantee that an Internet service provider is meeting or violating a contract.'),
      ],
    },
    {
      title: '5. Network Guidance',
      blocks: [
        paragraph('The App may provide general suggestions about router placement, Wi-Fi bands, channels, DNS, connected devices, and other network settings.'),
        paragraph('These suggestions are informational only. The App does not automatically modify your router, device, DNS, carrier account, or Internet service.'),
        paragraph('You are responsible for reviewing device and network documentation before changing any settings.'),
      ],
    },
    {
      title: '6. Advertisements',
      blocks: [
        paragraph('The App may display banner, rewarded, or other advertisements provided by Google Mobile Ads or advertising partners.'),
        paragraph('We do not control every advertisement, advertiser website, product, or service. Your interaction with an advertisement or third-party service is between you and the relevant third party.'),
      ],
    },
    {
      title: '7. Acceptable Use',
      blocks: [
        paragraph('You agree not to:'),
        list([
          'Use the App unlawfully or without authorization',
          'Test networks or systems in a way that violates applicable rules or agreements',
          'Attempt to interfere with, overload, disrupt, or damage the App or test infrastructure',
          'Reverse engineer, modify, redistribute, or exploit the App except where permitted by law',
          'Circumvent security, advertising, access, or usage controls',
          'Use automated systems to generate excessive or abusive tests',
          'Misrepresent test results as an official certification issued by us or M-Lab',
        ]),
        paragraph('We may restrict access to the App if we reasonably believe it is being misused.'),
      ],
    },
    {
      title: '8. Intellectual Property',
      blocks: [
        paragraph('The App, including its design, software, graphics, branding, and original content, is owned by or licensed to 2xHappiness and is protected by applicable intellectual-property laws.'),
        paragraph('These Terms grant you a limited, personal, non-exclusive, non-transferable, and revocable license to use the App for lawful personal purposes.'),
        paragraph('Third-party names, trademarks, services, and content remain the property of their respective owners.'),
      ],
    },
    {
      title: '9. Third-Party Services',
      blocks: [
        paragraph('The App may use or link to services provided by M-Lab, Google Firebase, Google Mobile Ads, and other third parties.'),
        paragraph('Third-party services are governed by their own terms and policies. We are not responsible for third-party availability, content, security, data practices, or actions.'),
      ],
    },
    {
      title: '10. Service Availability and Changes',
      blocks: [
        paragraph('We may update, modify, suspend, or discontinue any part of the App at any time.'),
        paragraph('We do not guarantee that:'),
        list([
          'The App will always be available',
          'Every test server will be reachable',
          'Every test will complete successfully',
          'The App will be compatible with every device or network',
          'Features will remain unchanged',
        ]),
      ],
    },
    {
      title: '11. Disclaimer of Warranties',
      blocks: [
        paragraph('To the maximum extent permitted by law, the App is provided “as is” and “as available.”'),
        paragraph('We disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, accuracy, availability, non-infringement, and uninterrupted operation.'),
        paragraph('Nothing in these Terms excludes rights that cannot legally be excluded under applicable consumer-protection law.'),
      ],
    },
    {
      title: '12. Limitation of Liability',
      blocks: [
        paragraph('To the maximum extent permitted by law, 2xHappiness will not be liable for indirect, incidental, special, consequential, or punitive damages arising from:'),
        list([
          'Use of or inability to use the App',
          'Inaccurate or incomplete measurements',
          'Mobile data, roaming, or Internet service charges',
          'Changes made to network or device settings',
          'Third-party services, advertisements, or websites',
          'Loss of data, revenue, business, or opportunity',
          'Network outages, interruptions, or security incidents',
        ]),
        paragraph('Our total liability will not exceed the amount you paid directly for the App during the twelve months before the event giving rise to the claim, unless applicable law requires otherwise.'),
      ],
    },
    {
      title: '13. Termination',
      blocks: [
        paragraph('You may stop using the App at any time by uninstalling it.'),
        paragraph('We may suspend or terminate access if you violate these Terms, misuse the App, or create a risk to users, infrastructure, or third parties.'),
        paragraph('Sections intended to survive termination—including intellectual property, disclaimers, and limitations of liability—will remain effective.'),
      ],
    },
    {
      title: '14. Governing Law',
      blocks: [
        paragraph('These Terms are governed by the laws applicable to the operator, without regard to conflict-of-law principles.'),
        paragraph('Any mandatory consumer rights or dispute-resolution rules applicable in your country remain unaffected.'),
      ],
    },
    {
      title: '15. Changes to These Terms',
      blocks: [
        paragraph('We may update these Terms when the App, applicable laws, or third-party services change.'),
        paragraph('The updated version will be published with a revised “Last Updated” date. Continued use of the App after an update constitutes acceptance of the revised Terms.'),
      ],
    },
    {
      title: '16. Contact Us',
      blocks: [
        paragraph('For questions about these Terms, contact:'),
        paragraph('2xHappiness'),
        paragraph('Email: yuw558071@gmail.com'),
        paragraph('Jurisdiction: Laws applicable to the operator.'),
      ],
    },
  ],
};
